import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, ArrowUpToLineIcon, PencilIcon, XIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  LoadingState,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import {
  AUDIENCE,
  AUDIENCE_LABELS,
  SCOPE,
  parseScope,
  whenLabel,
  type SkillFile,
  type SkillVersionEntry,
  type SkillsResponse,
} from '@/pages/assistant/shared';

/**
 * One skill, read-only: what it does, its instructions, its tools, and its
 * version history (toggle to any previous version; restoring carries the old
 * content into the edit page and saves as a NEW version). Editing lives on
 * the separate /edit route.
 */
export function SkillViewPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams<{ scope: string; name: string }>();
  const scope = parseScope(params.scope);
  const name = params.name ?? '';

  const [viewing, setViewing] = useState<SkillVersionEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const skill = useQuery({
    queryKey: ['assistant-skill', scope, name],
    queryFn: ({ signal }) =>
      getJson<SkillFile>('/assistant/skills/file', { name, scope }, signal),
  });
  const versions = useQuery({
    queryKey: ['assistant-skill-versions', scope, name],
    queryFn: ({ signal }) =>
      getJson<SkillVersionEntry[]>('/assistant/skills/versions', { name, scope }, signal),
  });
  const index = useQuery({
    queryKey: ['assistant-skills'],
    queryFn: ({ signal }) => getJson<SkillsResponse>('/assistant/skills', undefined, signal),
  });
  const canManageGlobal = index.data?.canManageGlobal === true;
  const canEdit = scope === SCOPE.user || canManageGlobal;

  const remove = useMutation({
    mutationFn: () =>
      sendJson('delete', `/assistant/skills/file?name=${encodeURIComponent(name)}&scope=${scope}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assistant-skills'] });
      navigate('/assistant/skills');
    },
    onError: (error) => {
      setDeleting(false);
      setNotice(errorMessage(error));
    },
  });

  /** Promote = write the global copy, then delete the personal one. */
  const promote = useMutation({
    mutationFn: async (file: SkillFile) => {
      await sendJson('put', '/assistant/skills/file', {
        name: file.name,
        scope: SCOPE.global,
        audience: AUDIENCE.internal,
        description: file.description,
        instructions: file.instructions,
        tools: file.tools,
      });
      await sendJson(
        'delete',
        `/assistant/skills/file?name=${encodeURIComponent(file.name)}&scope=${SCOPE.user}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assistant-skills'] });
      navigate(`/assistant/skills/${SCOPE.global}/${name}`, { replace: true });
      setPromoting(false);
    },
    onError: (error) => {
      setPromoting(false);
      setNotice(errorMessage(error));
    },
  });

  if (skill.isPending) return <LoadingState label="Loading skill…" />;
  if (skill.isError) {
    return <ErrorNotice title="Skill not found" message={errorMessage(skill.error)} />;
  }
  const file = skill.data;
  const shown = viewing ?? file;
  const history = versions.data ?? [];

  return (
    <section className="animate-portal-rise">
      <div className="flex items-start justify-between gap-4">
        <PageHeading
          eyebrow="AI ASSISTANT"
          title={file.name}
          subtitle={file.description}
        />
        <span className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/assistant/skills')}>
            <ArrowLeftIcon aria-hidden className="size-4" /> All skills
          </Button>
          {canManageGlobal && scope === SCOPE.user ? (
            <Button variant="secondary" onClick={() => setPromoting(true)}>
              <ArrowUpToLineIcon aria-hidden className="size-4" /> Promote
            </Button>
          ) : null}
          {canEdit ? (
            <>
              <Button
                variant="secondary"
                onClick={() => setDeleting(true)}
                aria-label="Delete skill"
              >
                <XIcon aria-hidden className="size-4" /> Delete
              </Button>
              <Button onClick={() => navigate(`/assistant/skills/${scope}/${name}/edit`)}>
                <PencilIcon aria-hidden className="size-4" /> Edit
              </Button>
            </>
          ) : null}
        </span>
      </div>

      {notice === null ? null : (
        <div className="mb-3">
          <ErrorNotice inline title="Something went wrong" message={notice} />
        </div>
      )}

      <Panel className="p-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Pill tone={scope === SCOPE.global ? 'info' : 'neutral'}>
            {scope === SCOPE.global ? 'Organization' : 'Personal'}
          </Pill>
          {scope === SCOPE.global ? <Pill>{AUDIENCE_LABELS[file.audience]}</Pill> : null}
          {history.length > 1 ? (
            <label className="ml-auto flex items-center gap-2 text-portal-micro font-medium text-portal-muted">
              Version
              <select
                value={viewing?.version ?? file.version}
                onChange={(event) => {
                  const selected = Number(event.target.value);
                  setViewing(
                    selected === file.version
                      ? null
                      : (history.find((entry) => entry.version === selected) ?? null),
                  );
                }}
                className="rounded-md border border-admin-line px-2 py-1 text-portal-small text-portal-ink focus:border-brand/60 focus:outline-none"
              >
                <option value={file.version}>v{file.version} (current)</option>
                {history
                  .filter((entry) => entry.version !== file.version)
                  .map((entry) => (
                    <option key={entry.version} value={entry.version}>
                      v{entry.version}
                      {whenLabel(entry.updatedAt) ? ` — ${whenLabel(entry.updatedAt)}` : ''}
                    </option>
                  ))}
              </select>
            </label>
          ) : null}
        </div>

        {viewing !== null ? (
          <p className="mb-3 rounded-md bg-portal-sky px-3 py-2 text-portal-micro font-medium text-brand">
            Viewing v{viewing.version} (read-only) — the current version is v{file.version}.
          </p>
        ) : null}

        <div className="space-y-4">
          <div>
            <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
              When to use it
            </span>
            <p className="text-portal-small">{shown.description}</p>
          </div>
          <div>
            <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
              Instructions
            </span>
            <pre className="rounded-md border border-admin-line bg-portal-bg p-3 font-mono text-portal-micro leading-relaxed whitespace-pre-wrap">
              {shown.instructions}
            </pre>
          </div>
          {shown.tools.length > 0 ? (
            <div>
              <span className="mb-1 block text-portal-micro font-medium text-portal-muted">
                Tools this skill uses
              </span>
              <span className="flex flex-wrap gap-1.5">
                {shown.tools.map((tool) => (
                  <Pill key={tool}>{tool}</Pill>
                ))}
              </span>
            </div>
          ) : null}
        </div>

        {viewing !== null && canEdit ? (
          <div className="mt-5 flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setViewing(null)}>
              Back to current
            </Button>
            <Button
              onClick={() =>
                navigate(`/assistant/skills/${scope}/${name}/edit`, {
                  state: { restore: viewing },
                })
              }
            >
              Restore this version
            </Button>
          </div>
        ) : null}
      </Panel>

      <ConfirmDialog
        open={deleting}
        tone="danger"
        title="Delete this skill?"
        message={`"${name}" will be removed for good.`}
        confirmLabel="Delete skill"
        onConfirm={() => remove.mutate()}
        onCancel={() => {
          if (!remove.isPending) setDeleting(false);
        }}
        pending={remove.isPending}
      />
      <ConfirmDialog
        open={promoting}
        title="Promote to organization?"
        message={`"${name}" becomes an org-wide skill (internal only) and leaves your personal list. You can change its audience afterwards.`}
        confirmLabel="Promote skill"
        onConfirm={() => promote.mutate(file)}
        onCancel={() => {
          if (!promote.isPending) setPromoting(false);
        }}
        pending={promote.isPending}
      />
    </section>
  );
}
